import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { Link } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { TableContainer } from '@/buyer-portal/components/features/shared'

/**
 * Same first-column width as listings filters (`FilterContainer` sidebar: 11.75rem)
 * and the same 1.5rem track gap as `ListingsOuterContainer`, instead of the default
 * `TableContainer` 18rem / 2-column contract grid used on listing detail pages.
 *
 * The media query is not redundant with the one `TableContainer` already carries. Extending a
 * styled component appends its rules after the base's, and a media query adds no specificity, so
 * the unconditional declaration above beat the base's `1fr` at every width — leaving the title a
 * 188px column and the tab's whole content 146px on a 390px phone.
 */
export const StorefrontTableContainer = styled(TableContainer)`
  grid-template-columns: 11.75rem 1fr;

  & > *:nth-child(2) {
    min-width: 0;
  }

  @media ${Breakpoints.max.medium} {
    grid-template-columns: 1fr;
    grid-gap: 0;
  }
`

export const InfoSection = styled.div`
  position: relative;
  margin-bottom: 5rem;
`

export const Title = styled.h1`
  max-width: 100%;
  margin-top: 0.75rem;
  color: ${Colors.Semantic.Neutral.black};
  ${Typography.Title2}
`

export const Container = styled.div`
  ${Typography.Body2}
  margin-top: 1.5rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`

export const SubHeader = styled.h2`
  ${Typography.Title3}
  margin: 0.5rem 0 1rem 0;
`

/** Bold section label used under storefront content tabs (e.g. Shipping). */
export const SectionLabel = styled.h3`
  ${Typography.Body2Semibold}
  margin: 1.25rem 0 0.5rem 0;
`

export const TextBold = styled.span`
  ${Typography.Body2Semibold}
`
export const TextItalic = styled.span`
  font-style: italic;
`

export const StyledLink = Link

export const StyledParagraph = styled.p`
  margin-bottom: 1rem;
`
export const StyledInnerParagraph = styled.p`
  margin: 0;
`

export const List = styled.ul`
  margin-bottom: 1rem;
  padding-left: 1rem;

  /* Target li directly to override global CSS reset */
  li {
    list-style-type: disc !important;
  }
`

export const OrderedList = styled.ol`
  margin-bottom: 1rem;
  padding-left: 1rem;

  /* Target li directly to override global CSS reset */
  li {
    list-style-type: decimal !important;
  }
`

export const SubList = styled.ul`
  margin-top: 0.5rem;
  padding-left: 1rem;

  li {
    list-style-type: circle !important;
  }
`

export const ListItem = styled.li`
  margin-bottom: 0.5rem;
  margin-left: 1rem;
`

export const StyledAccordionWrapper = styled.div`
  & > div {
    padding-left: 1rem;
    @media ${Breakpoints.max.medium} {
      margin-top: 2rem;
    }
  }
  & > div > div:first-child {
    padding: 1rem 0;
    /* border-bottom: 1px solid ${Colors.Semantic.Neutral.light_grey}; */
    font-weight: 700;
  }
  & > div > div:last-child {
    background-color: ${Colors.Semantic.Neutral.light_grey};
  }
`

export const AnswerContainer = styled.div`
  padding: 0.5rem 1rem;
`

export const StyledIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Info.highlight};
`

export const QuestionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const Section = styled.div`
  border-bottom: 1px solid ${Colors.Semantic.Neutral.light_grey};
  margin-bottom: 1rem;
`

const ShippingTableContainer = styled.div`
  display: flex;
  gap: 2rem;
  margin: 1.5rem 0 0 0;

  @media ${Breakpoints.max.medium} {
    flex-direction: column;
    gap: 1.5rem;
  }
`

export const TableColumn = styled.div`
  flex: 1;
`

/**
 * One grade row: the grade beside its description, stacking to grade-above-description below
 * `medium`. Extends the flex container rather than restating it — the margin reset is because
 * consecutive rows are a list, not one block set off from the copy above it.
 */
export const ConditionGradeRow = styled(ShippingTableContainer)`
  margin: 0;
  gap: 1rem;
  padding: 0.75rem 0;
  border-top: 0.0625rem solid ${Colors.Semantic.Neutral.light_grey};
`

/**
 * The grade label column. `flex: none` on mobile because the row turns into a column there, where
 * a `28%` basis would be read as height rather than width.
 */
export const ConditionGradeLabel = styled.div`
  ${Typography.Body2Semibold}
  flex: 0 0 28%;
  max-width: 12rem;

  @media ${Breakpoints.max.medium} {
    flex: none;
    max-width: none;
  }
`

/** Two-column grade table: gray centered label | white bulleted details. */
export const ConditionGradeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0 0 0;
  table-layout: fixed;

  th,
  td {
    border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
    vertical-align: middle;
  }

  th {
    width: 28%;
    background-color: ${Colors.Semantic.Neutral.light_grey};
    color: ${Colors.Semantic.Neutral.black};
    ${Typography.Body2Semibold}
    text-align: center;
    padding: 1rem 0.75rem;
  }

  td {
    width: 72%;
    background-color: ${Colors.Semantic.Neutral.white};
    color: ${Colors.Semantic.Neutral.black};
    ${Typography.Body2}
    text-align: left;
    padding: 0.75rem 1rem;
  }

  /* Tighten list spacing inside grade cells */
  ul {
    margin: 0;
    padding-left: 1.25rem;
  }

  li {
    margin-bottom: 0.35rem;
    margin-left: 0;
  }

  li:last-child {
    margin-bottom: 0;
  }

  /*
   * Same treatment the CMS tables get below: the grade label becomes a full-width heading and its
   * criteria sit under it. A fixed 28% label column leaves the criteria about 100px on a phone,
   * which wraps every bullet to one word per line.
   */
  @media ${Breakpoints.max.medium} {
    display: block;
    table-layout: auto;

    tbody,
    tr,
    th,
    td {
      display: block;
      width: 100%;
    }

    th {
      text-align: left;
    }

    tr {
      margin-bottom: 1rem;
    }
  }
`

export const ConditionGradeHeaderCell = styled.th`
  && {
    vertical-align: top;
  }
`
export const TableNote = styled.p`
  font-style: italic;
  margin: 0.5rem 0 1rem 0;
  color: #666;
`
